import { Box, StackProps, HStack, Button, Center } from "@chakra-ui/react";
import Avatar from "components/Avatar";
import BoldSpinner from "components/BoldSpinner";
import FancyBearImage from "components/FancyBearImage";

import TokenSelect from "components/TokenSelect";
import { fancyBearsContract } from "config";
import useAssets from "hooks/useAssets";
import useDressingRoom from "hooks/useDressingRoom";
import useTranslate from "hooks/useTranslate";
import { useCallback } from "react";
import { useNavigate } from "react-router-dom";

type BearSelectorContainerProps = StackProps & {
  basePath: string;
  isLoading: boolean;
};

const BearSelectorContainer = ({
  basePath,
  isLoading,
  ...rest
}: BearSelectorContainerProps) => {
  const translate = useTranslate();
  const { fancyBearsInWallet } = useAssets();
  const navigate = useNavigate();
  const { tokenId } = useDressingRoom();

  const isOwner =
    fancyBearsInWallet && tokenId
      ? fancyBearsInWallet.indexOf(tokenId) >= 0
      : undefined;

  const handleChange = useCallback(
    (tokenId?: number) => {
      if (tokenId) {
        navigate(`${basePath}/${tokenId}`);
      }
    },
    [basePath, navigate]
  );

  return tokenId ? (
    <HStack spacing="3" w="full" justify="space-between" {...rest}>
      <HStack spacing="3">
        <Avatar size="16" position="relative">
          <FancyBearImage
            tokenId={tokenId}
            objectFit="cover"
            opacity={isLoading ? 0.5 : 1}
            transition="all 0.15s"
          />

          {isLoading && (
            <Center
              position="absolute"
              w="full"
              h="full"
              zIndex="1"
              top="0"
              left="0"
            >
              <BoldSpinner size="md" thickness="3px" />
            </Center>
          )}
        </Avatar>

        <Box
          w={{
            base: "8rem",
            sm: "9.2rem",
          }}
        >
          <TokenSelect
            tokenId={tokenId}
            defaultTokens={fancyBearsInWallet || []}
            onChange={handleChange}
            chakraStyles={{
              control: (provided) => ({
                ...provided,
                fontWeight: "bold",
                border: "none",
                fontSize: {
                  base: "2xl",
                  sm: "3xl",
                },
                p: "0",
                borderBottom: "2px solid",
              }),
              valueContainer: (provided) => ({
                ...provided,
                p: "0",
              }),
              dropdownIndicator: (provided) => ({
                ...provided,
                px: "0",
                bg: "transparent",
              }),
              downChevron: (provided) => ({
                ...provided,
                width: "9",
                height: "auto",
              }),
            }}
          />
        </Box>
      </HStack>

      {isOwner === false && (
        <a
          href={`https://opensea.io/assets/${fancyBearsContract.address}/${tokenId}`}
          target="_blank"
          rel="noreferrer"
        >
          <Button as="span" size="sm" variant="outline" ml="auto">
            {translate("common:buyOnOpenSea")}
          </Button>
        </a>
      )}
    </HStack>
  ) : null;
};

export default BearSelectorContainer;
